import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { useFeatureFlags } from "@/components/FeatureFlags";
import { Separator } from "@/components/Separator";
import { ConsultantProfileContext, UserContext } from "@/components/User";
import { isProduction } from "@/core/environments";
import { FEATURE_FLAG } from "@/data/feature-flags";
import { Flex } from "@/eworkitComponents";
import { Actions, IActionsRenderProps } from "@/views/components/Actions";
import { router } from "@/views/router";

import { MobileMenuIcon } from "./MobileMenuIcon";
import { RouteNavItem } from "./RouteNavItem";

interface INavigationMobileProps {
  onOpen?: () => void;
  onClose?: () => void;
}

export const NavigationMobile: FC<INavigationMobileProps> = ({ onOpen, onClose }) => {
  const User = useContext(UserContext);
  const ConsultantProfile = useContext(ConsultantProfileContext);
  const isManager = User.hasCompanyManagerPrivileges();
  const { isFeatureEnabled } = useFeatureFlags();

  const { t } = useTranslation("session");

  const baseClass = "navigation";

  const dashboardRoute = router.get("dashboard");
  const jobRequestsRoute = router.get("job-requests");
  const jobApplicationsRoute = router.get("job-applications");
  const timeReportsRoute = router.get("time-reports");
  const assignmentsRoute = router.get("assignments");
  const notificationsRoute = router.get("notifications");
  const chatRoute = router.get("message-center");
  const myProfileRoute = router.get("consultant-profiles.detail", {
    consultantProfileId: ConsultantProfile.currentProfileId,
  });
  const myTeamRoute = router.get("companies.team");
  const settingsRoute = router.get("user.settings");

  const renderActionsTarget = useCallback(
    (actionRenderProps: IActionsRenderProps) => <MobileMenuIcon isOpen={actionRenderProps.open} />,
    [],
  );

  return (
    <Actions
      className={baseClass}
      placement="bottom-start"
      renderTarget={renderActionsTarget}
      onClose={onClose}
      onOpen={onOpen}>
      <Flex direction="column" className={`${baseClass}__mobile-content`}>
        <Flex direction="column" padding={["md", "md", "xs", "md"]}>
          <RouteNavItem
            variant="mobile"
            text={t("COMPONENTS.TOPBAR.BUTTON_DASHBOARD")}
            path={dashboardRoute.path}
            exact={false}
            testName={dashboardRoute.testName}
          />

          <RouteNavItem
            variant="mobile"
            text={t("COMPONENTS.TOPBAR.BUTTON_JOB_REQUESTS")}
            path={jobRequestsRoute.path}
            exact={false}
            testName={jobRequestsRoute.testName}
          />

          <RouteNavItem
            variant="mobile"
            text={t("COMPONENTS.TOPBAR.BUTTON_APPLICATIONS")}
            path={jobApplicationsRoute.path}
            exact={false}
            testName={jobApplicationsRoute.testName}
          />

          {!isProduction && User.contextCompany && (
            <RouteNavItem
              variant="mobile"
              text={t("COMPONENTS.TOPBAR.BUTTON_TIME_REPORTS")}
              path={timeReportsRoute.path}
              exact={false}
              testName={timeReportsRoute.testName}
            />
          )}

          {User.hasRouteAccess(assignmentsRoute) && (
            <RouteNavItem
              variant="mobile"
              text={t("COMPONENTS.TOPBAR.BUTTON_ASSIGNMENTS")}
              path={assignmentsRoute.path}
              exact={false}
              testName={assignmentsRoute.testName}
            />
          )}

          {User.hasRouteAccess(chatRoute) &&
            isFeatureEnabled(FEATURE_FLAG.MESSAGE_CENTER) &&
            (!User.contextCompany || User.hasCompanyManagerPrivileges()) && (
              <RouteNavItem
                variant="mobile"
                text={t("COMPONENTS.TOPBAR.BUTTON_CHAT")}
                path={chatRoute.path}
                exact={false}
                testName={chatRoute.testName}
              />
            )}

          {User.hasRouteAccess(notificationsRoute) && (
            <RouteNavItem
              variant="mobile"
              text={t("COMPONENTS.TOPBAR.BUTTON_MESSAGE_CENTER")}
              path={notificationsRoute.path}
              exact={false}
              testName={notificationsRoute.testName}
            />
          )}
        </Flex>

        <Separator />

        <Flex direction="column" padding={["xs", "md"]}>
          {!isManager && (
            <RouteNavItem
              variant="mobile"
              icon="user"
              text={t("COMPONENTS.TOPBAR.BUTTON_MY_PROFILE")}
              path={myProfileRoute.path}
              exact={false}
              testName={myProfileRoute.testName}
            />
          )}

          {isManager && (
            <RouteNavItem
              variant="mobile"
              icon="users"
              text={t("COMPONENTS.TOPBAR.BUTTON_MY_COMPANY")}
              path={myTeamRoute.path}
              exact={false}
              testName={myTeamRoute.testName}
            />
          )}

          <RouteNavItem
            variant="mobile"
            icon="settings"
            text={t("COMPONENTS.TOPBAR.BUTTON_SETTINGS")}
            path={settingsRoute.path}
            exact={false}
            testName={settingsRoute.testName}
          />
        </Flex>
      </Flex>
    </Actions>
  );
};
